import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import { toast } from "sonner";

type Signup = {
  id: string;
  full_name: string;
  email: string;
  sport: string | null;
  team: string | null;
  league: string | null;
  level: string | null;
  social_handle: string | null;
  message: string | null;
  status: string;
  invited_at: string | null;
  athlete_id: string | null;
  created_at: string;
};

const AdminSignups = () => {
  const [list, setList] = useState<Signup[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("athlete_signups")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    setList((data as any) ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const approveAndInvite = async (s: Signup) => {
    if (!confirm(`Approve ${s.full_name} and email them an invite?`)) return;
    setBusyId(s.id);
    try {
      const { data, error } = await supabase.functions.invoke("invite-athlete", {
        body: { signup_id: s.id, redirect_to: `${window.location.origin}/set-password` },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      toast.success("Invite sent and profile created");
      load();
    } catch (e: any) {
      toast.error(e.message ?? "Invite failed");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (s: Signup) => {
    if (!confirm(`Reject ${s.full_name}?`)) return;
    const { error } = await supabase.from("athlete_signups").update({ status: "rejected" }).eq("id", s.id);
    if (error) return toast.error(error.message);
    load();
  };

  return (
    <AdminLayout>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="font-display text-3xl">Athlete Signups</h1>
          <p className="text-sm text-muted-foreground mt-1">{list.length} total</p>
        </div>
      </div>
      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : (
        <div className="grid gap-3">
          {list.map((s) => (
            <div key={s.id} className="bg-card rounded-2xl p-4 shadow-card">
              <div className="flex items-start gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold">{s.full_name}</p>
                    <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${
                      s.status === "approved" ? "bg-primary text-primary-foreground" :
                      s.status === "rejected" ? "bg-destructive text-destructive-foreground" :
                      "bg-muted text-muted-foreground"
                    }`}>
                      {s.status}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">{s.email}</p>
                  <p className="text-sm mt-1">
                    {[s.sport, s.team, s.league, s.level].filter(Boolean).join(" · ")}
                  </p>
                  {s.social_handle && <p className="text-xs text-muted-foreground mt-1">{s.social_handle}</p>}
                  {s.message && <p className="text-sm mt-2 whitespace-pre-wrap text-muted-foreground">{s.message}</p>}
                  <p className="text-[10px] text-muted-foreground mt-2">
                    Submitted {new Date(s.created_at).toLocaleString()}
                    {s.invited_at && ` · Invited ${new Date(s.invited_at).toLocaleString()}`}
                  </p>
                </div>
                <div className="flex gap-2">
                  {s.status !== "approved" && (
                    <button
                      onClick={() => approveAndInvite(s)}
                      disabled={busyId === s.id}
                      className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50"
                    >
                      {busyId === s.id ? "Sending…" : "Approve & invite"}
                    </button>
                  )}
                  {s.status === "pending" && (
                    <button onClick={() => reject(s)} className="px-4 py-2 rounded-full bg-muted text-sm font-semibold text-destructive">
                      Reject
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
          {list.length === 0 && <p className="text-muted-foreground">No signups yet.</p>}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminSignups;
